"use strict";

const baseTemplate = document.createElement("template");
baseTemplate.innerHTML = require("./template");

const DEFAULT_LABEL_LEFT = "Off";
const DEFAULT_LABEL_RIGHT = "On";

/**
 * swiftref-split-toggle web component
 *
 * A context-agnostic split-button toggle that displays two mutually exclusive
 * options side by side. It carries no business knowledge: which half is "on",
 * what text each half shows, and its accessible name are all configurable.
 *
 * Semantics:
 *   - checked = false  -> left half active (off)
 *   - checked = true   -> right half active (on)
 *
 * Configurable attributes:
 *   - name        : identifier for the form field
 *   - checked     : boolean state (true = right/on)
 *   - label-left  : text for the left half (default "Off")
 *   - label-right : text for the right half (default "On")
 *   - message     : validation error message (rendered via internal swiftref-message)
 *   - disabled    : disables both halves
 *   - aria-label  : optional explicit accessible name; when omitted one is
 *                   derived from the two labels
 *
 * Events:
 *   - dispatches a bubbling "change" CustomEvent with detail.checked
 */
class Element extends HTMLElement {

    static get observedAttributes() {
        return ["name", "checked", "message", "disabled", "label-left", "label-right", "aria-label"];
    }

    constructor() {
        super();

        this._checked = false;

        this.root = this.attachShadow({ mode: "open" });
        this.root.appendChild(baseTemplate.content.cloneNode(true));

        this.splitToggle = this.root.querySelector(".swiftref-split-toggle");
        this.leftButton = this.root.querySelector(".swiftref-split-toggle__half--left");
        this.rightButton = this.root.querySelector(".swiftref-split-toggle__half--right");
        this.messageElement = this.root.querySelector("swiftref-message");

        this.leftButton.addEventListener("click", (e) => {
            e.preventDefault();
            this._setChecked(false);
        });

        this.rightButton.addEventListener("click", (e) => {
            e.preventDefault();
            this._setChecked(true);
        });
    }

    connectedCallback() {
        this._render();
    }

    // ── Public API ──────────────────────────────────────────────

    get name() {
        return this.getAttribute("name");
    }
    set name(value) {
        this.setAttribute("name", value);
    }

    get checked() {
        return this._checked;
    }
    set checked(value) {
        this._checked = Boolean(value);
        if (this._checked) {
            this.setAttribute("checked", "");
        } else {
            this.removeAttribute("checked");
        }
        this._render();
    }

    get message() {
        return this.getAttribute("message");
    }
    set message(value) {
        if (value) {
            this.setAttribute("message", value);
        } else {
            this.removeAttribute("message");
        }
    }

    get disabled() {
        return this.hasAttribute("disabled");
    }
    set disabled(value) {
        if (value) {
            this.setAttribute("disabled", "");
        } else {
            this.removeAttribute("disabled");
        }
    }

    get labelLeft() {
        return this.getAttribute("label-left") || DEFAULT_LABEL_LEFT;
    }
    set labelLeft(value) {
        this.setAttribute("label-left", value);
    }

    get labelRight() {
        return this.getAttribute("label-right") || DEFAULT_LABEL_RIGHT;
    }
    set labelRight(value) {
        this.setAttribute("label-right", value);
    }

    get ariaLabel() {
        return this.getAttribute("aria-label");
    }
    set ariaLabel(value) {
        this.setAttribute("aria-label", value);
    }

    // ── Lifecycle ───────────────────────────────────────────────

    attributeChangedCallback(attrName, oldValue, newValue) {
        if (oldValue === newValue) {
            return;
        }
        if (attrName === "checked") {
            this._checked = newValue === "" || newValue === "true" || newValue === "on";
        }
        this._render();
    }

    // ── Internals ───────────────────────────────────────────────

    _setChecked(value) {
        if (this._checked === value) {
            return;
        }
        this.checked = value;
        this.dispatchEvent(new CustomEvent("change", {
            bubbles: true,
            detail: { checked: value }
        }));
    }

    _render() {
        if (!this.splitToggle || !this.leftButton || !this.rightButton) {
            return;
        }

        // Labels (configurable, with generic defaults).
        this.leftButton.textContent = this.labelLeft;
        this.rightButton.textContent = this.labelRight;

        // Accessible name: explicit attribute wins, otherwise derive it.
        const ariaLabel = this.getAttribute("aria-label")
            || `Toggle between ${this.labelLeft} and ${this.labelRight}`;
        this.splitToggle.setAttribute("aria-label", ariaLabel);

        // Active half.
        if (this._checked) {
            this.leftButton.classList.remove("swiftref-split-toggle__half--active");
            this.leftButton.setAttribute("aria-pressed", "false");
            this.rightButton.classList.add("swiftref-split-toggle__half--active");
            this.rightButton.setAttribute("aria-pressed", "true");
        } else {
            this.leftButton.classList.add("swiftref-split-toggle__half--active");
            this.leftButton.setAttribute("aria-pressed", "true");
            this.rightButton.classList.remove("swiftref-split-toggle__half--active");
            this.rightButton.setAttribute("aria-pressed", "false");
        }

        // Disabled state.
        const disabled = this.disabled;
        this.splitToggle.setAttribute("aria-disabled", disabled ? "true" : "false");
        this.leftButton.disabled = disabled;
        this.rightButton.disabled = disabled;

        // Error message.
        const message = this.getAttribute("message");
        if (message) {
            this.messageElement.setAttribute("message", message);
        } else {
            this.messageElement.removeAttribute("message");
        }

        // Keep the toggle (and therefore its two labels) at exactly the same
        // width whether or not a validation message is shown. The host is an
        // inline-block sized to its content, and the toggle fills it at
        // width:100%; a long error message would otherwise stretch the host to
        // the message's width and change the labels' proportions. Pin the host
        // to the toggle's natural content width while a message is present so
        // the message wraps below within that width; clear it otherwise.
        if (message) {
            this.style.width = this._measureNaturalToggleWidth() + "px";
        } else {
            this.style.width = "";
        }
    }

    _measureNaturalToggleWidth() {
        // Size the toggle to its content (max-content) to obtain its intrinsic
        // width, independent of the host width that a wide error message would
        // otherwise inflate. All reads/writes are synchronous, so no repaint
        // occurs before the width is restored.
        const toggle = this.splitToggle;
        const previousWidth = toggle.style.width;
        toggle.style.width = "max-content";
        const width = toggle.getBoundingClientRect().width;
        toggle.style.width = previousWidth || "";
        return width;
    }
}

if (!customElements.get("swiftref-split-toggle")) {
    window.customElements.define("swiftref-split-toggle", Element);
}

module.exports = Element;
